import { getEvent } from '@/service/v3/endpoints/events.ts'
import type { MaybeRef } from '@/hooks/usePolicyTypeSelection.ts'
import { unref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { QUERY_KEY_EVENT } from '@/queries/events/eventKeys.ts'
import { orderBy } from 'lodash-es'
import moment from 'moment/moment'

export type EventStatusFilter = 'all' | 'upcoming' | 'past'

export const useEventQuery = (
  eventId: MaybeRef<number>,
  eventStatusFilter: MaybeRef<EventStatusFilter> = 'all'
) => {
  return useQuery({
    queryKey: [QUERY_KEY_EVENT, eventId, eventStatusFilter],
    queryFn: () => getEvent(unref(eventId)),
    placeholderData: {
      data: {
        id: '0',
        attributes: {},
      },
      included: [],
    },

    select: (data) => {
      // Courses is coming in the included field which we map and attach to the event object
      const { included: includedCourses } = data
      //
      const courses = includedCourses?.map((course: any) => ({
        id: course.id as number,
        ...course.attributes,
      }))

      // NOTE! We are doing client-side filtering and sorting here since the API does not support it, and will soon move to graphql
      // This is the layer where all of this should ideally happen
      const filteredCourses = filterCoursesAsIfSupportedByAPI(
        courses,
        unref(eventStatusFilter)
      )

      return {
        id: data.id,
        courses: filteredCourses,
        ...data.data.attributes,
      }
    },
  })
}

const filterCoursesAsIfSupportedByAPI = (
  courses: Array<any> | undefined, // We have no types for this yet...
  eventStatusFilter: EventStatusFilter
) => {
  if (!courses) return []
  const now = new Date(Date.now()).getTime()

  // Sort courses by start_datetime in descending order
  // Should have come from the API, but since it doesn't, i'll stick to the client-side sorting
  const sortedCourses = orderBy(
    courses,
    [(course) => moment(course.start_datetime).valueOf()],
    ['desc']
  )

  if (eventStatusFilter === 'all') {
    return sortedCourses
  }

  // Apply filters, for now just upcoming and completed
  // Again should come from the API
  return sortedCourses.filter((course) => {
    // TODO: Ensure that time is calculated correctly (how do we treat local time zones here??)
    const eventEndTime = new Date(course.end_datetime).getTime()
    const isCourseOver = eventEndTime < now

    if (eventStatusFilter === 'upcoming') {
      return !isCourseOver
    } else if (eventStatusFilter === 'past') {
      return isCourseOver
    } else {
      // No filter - ei. all courses
      return true
    }
  })
}
